import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  CheckCircleIcon,
  CheckIcon,
  ReloadIcon,
  StarIcon,
} from "../../../../images/icons/icons";
import CalendarLoader from "../../../common-components/CalendarLoader";

const TMEAttendanceKeyInsights = ({ callFunction }) => {
  const {
    tmeCheckinAnalysis,
    tmeCheckinAnalysisError,
    tmeCheckinAnalysisLoading,
  } = useSelector((state) => state.prospects);

  const [insights, setInsights] = useState([]);

  const getIcon = (iconType) => {
    switch (iconType) {
      case "check":
        return <CheckIcon />;
      case "star":
        return <StarIcon />;
      default:
        return <CheckCircleIcon />;
    }
  };

  useEffect(() => {
    if (
      !tmeCheckinAnalysisLoading &&
      !tmeCheckinAnalysisError &&
      tmeCheckinAnalysis?.status &&
      tmeCheckinAnalysis?.stats
    ) {
      const stats = tmeCheckinAnalysis.stats;
      const generatedInsights = [];

      // Get both present data
      const bothPresentData = stats?.both_present || {};
      const bothPresentCount = bothPresentData.count || 0;
      const bothPresentPercentage = bothPresentData.percentage || 0;
      const totalDaysData = stats?.total_days || {};
      const totalDays = totalDaysData.count || 0;

      // Alignment Insight - based on both present percentage (always show)
      const alignmentRate = bothPresentPercentage;

      let alignmentLevel = "";
      let alignmentIcon = "";
      let alignmentIconBg = "";
      let alignmentIconColor = "";
      let alignmentCardBg = "";

      if (alignmentRate >= 85) {
        alignmentLevel = "Strong Alignment";
        alignmentIcon = "check";
        alignmentIconBg = "#d1fae5";
        alignmentIconColor = "#10b981";
        alignmentCardBg = "#f0fdf4";
      } else if (alignmentRate >= 50) {
        alignmentLevel = "Moderate Alignment";
        alignmentIcon = "check";
        alignmentIconBg = "#fef3c7";
        alignmentIconColor = "#f59e0b";
        alignmentCardBg = "#fefce8";
      } else {
        alignmentLevel = "Low Alignment";
        alignmentIcon = "check";
        alignmentIconBg = "#fee2e2";
        alignmentIconColor = "#ef4444";
        alignmentCardBg = "#fef2f2";
      }

      generatedInsights.push({
        type: "alignment",
        icon: alignmentIcon,
        iconBg: alignmentIconBg,
        iconColor: alignmentIconColor,
        cardBg: alignmentCardBg,
        title: alignmentLevel,
        description: `Member and trainer were both present on ${bothPresentCount} out of ${totalDays} days (${alignmentRate}% alignment rate)`,
      });

      // Satisfaction Insight - based on average rating
      const avgRatingData = stats.avg_rating || {};
      const avgRating = avgRatingData.value || 0;

      let satisfactionLevel = "";
      let satisfactionIconBg = "";
      let satisfactionIconColor = "";
      let satisfactionCardBg = "";

      // Rating thresholds: 4.5+ = High, 3.5+ = Moderate, < 3.5 = Low
      if (avgRating >= 4.5) {
        satisfactionLevel = "High Satisfaction";
        satisfactionIconBg = "#fef3c7";
        satisfactionIconColor = "#f59e0b";
        satisfactionCardBg = "#fefce8";
      } else if (avgRating >= 3.5) {
        satisfactionLevel = "Moderate Satisfaction";
        satisfactionIconBg = "#dbeafe";
        satisfactionIconColor = "#3b82f6";
        satisfactionCardBg = "#eff6ff";
      } else {
        satisfactionLevel = "Low Satisfaction";
        satisfactionIconBg = "#fee2e2";
        satisfactionIconColor = "#ef4444";
        satisfactionCardBg = "#fef2f2";
      }

      generatedInsights.push({
        type: "satisfaction",
        icon: "star",
        iconBg: satisfactionIconBg,
        iconColor: satisfactionIconColor,
        cardBg: satisfactionCardBg,
        title: satisfactionLevel,
        description: `Average rating of ${avgRating} stars indicates ${satisfactionLevel.toLowerCase()} member satisfaction`,
      });

      setInsights(generatedInsights);
    } else {
      setInsights([]);
    }
  }, [tmeCheckinAnalysisLoading, tmeCheckinAnalysisError, tmeCheckinAnalysis]);

  return (
    <div className="tme-analysis-insights-container mt30">
      <h3 className="tm-tb-header-title">
        Key Insights
        <button
          className="page-heading-reload-btn"
          onClick={callFunction}
          title="Reload"
          type="button"
        >
          <ReloadIcon />
        </button>
      </h3>

      <div className="tme-analysis-insights-grid mt15">
        {tmeCheckinAnalysisLoading ? (
          <CalendarLoader daysCount={2} shiftsPerDay={1} />
        ) : (
          insights.map((insight, index) => (
            <div
              key={index}
              className="tme-analysis-insights-card"
              style={{ backgroundColor: insight.cardBg }}
            >
              <div className="tme-analysis-insights-content">
                <div
                  className="tme-analysis-insights-icon"
                  style={{
                    backgroundColor: insight.iconBg,
                    color: insight.iconColor,
                  }}
                >
                  {getIcon(insight.icon)}
                </div>
                <div className="tme-analysis-insights-text">
                  <h4 className="tme-analysis-insights-title">
                    {insight.title}
                  </h4>
                  <p className="tme-analysis-insights-description">
                    {insight.description}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default TMEAttendanceKeyInsights;
